import { useEffect, useRef } from "react";

// C4 : comportements communs des modales globales — Escape, focus trap,
// restauration du focus à la fermeture, verrouillage du scroll de la page.
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

// Cartes des modales ouvertes. Une modale qui en contient une autre (modale
// locale ouverte dans le dossier affiché par la modale globale) lui laisse le
// clavier : Escape ne ferme que celle du dessus.
const cartesOuvertes = new Set();
const contientUneModaleOuverte = (carte) =>
  Array.from(cartesOuvertes).some((autre) => autre !== carte && carte.contains(autre));

const useModalA11y = ({ isOpen, enabled = true, canClose = true, onClose, cardRef }) => {
  const previouslyFocused = useRef(null);
  const previousBodyOverflow = useRef("");

  useEffect(() => {
    if (!isOpen || !enabled) return;
    previouslyFocused.current = document.activeElement;
    previousBodyOverflow.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const carte = cardRef.current;
    if (carte) {
      carte.focus();
      cartesOuvertes.add(carte);
    }

    const onKeyDown = (e) => {
      if (carte && contientUneModaleOuverte(carte)) return;
      if (e.key === "Escape" && canClose) {
        onClose();
        return;
      }
      if (e.key === "Tab" && cardRef.current) {
        const focusables = Array.from(
          cardRef.current.querySelectorAll(FOCUSABLE)
        ).filter((el) => el.offsetParent !== null);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      cartesOuvertes.delete(carte);
      document.body.style.overflow = previousBodyOverflow.current;
      if (previouslyFocused.current && previouslyFocused.current.focus) {
        previouslyFocused.current.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, enabled, canClose]);
};

export default useModalA11y;
